import { useState } from 'react';
import { X, Send, Loader2 } from 'lucide-react';
import axios from 'axios';
import { API_BASE } from '../config/api';

const API = API_BASE;

const TYPE_CONFIG = {
  booking_call: {
    title: 'Book a Call',
    subtitle: 'Schedule a discovery call with Natasha',
    fields: ['name', 'email', 'phone', 'message'],
  },
  business: {
    title: 'Business Inquiry',
    subtitle: 'Learn about business coaching & DIS programs',
    fields: ['name', 'email', 'phone', 'message'],
  },
  workshop: {
    title: 'Book a Workshop',
    subtitle: 'Inquire about team or salon workshops',
    fields: ['name', 'email', 'phone', 'message'],
  },
  speaking: {
    title: 'Speaking Engagement',
    subtitle: 'Invite Natasha to speak at your event',
    fields: ['name', 'email', 'phone', 'message'],
  },
  personal_dev: {
    title: 'Personal Development',
    subtitle: 'Explore mindset coaching and personal growth',
    fields: ['name', 'email', 'phone', 'message'],
  },
};

const PLACEHOLDERS = {
  booking_call: 'Tell us about yourself and what you\'re looking for...',
  business: 'Your business name, type of beauty business, current goals or challenges...',
  workshop: 'Organization, team size, preferred timing, anything else we should know...',
  speaking: 'Event type, date, audience, location, and any additional details...',
  personal_dev: 'What you\'re interested in, your goals, and the best way to reach you...',
};

export const ContactFormModal = ({ isOpen, onClose, inquiryType = 'booking_call', sourcePage = '' }) => {
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  if (!isOpen) return null;

  const config = TYPE_CONFIG[inquiryType] || TYPE_CONFIG.booking_call;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setResult(null);
    try {
      const { data } = await axios.post(`${API}/api/inquiries`, {
        ...form,
        inquiry_type: inquiryType,
        source_page: sourcePage,
      });
      setResult({ success: true, message: data.message });
      setForm({ name: '', email: '', phone: '', message: '' });
    } catch {
      setResult({ success: false, message: 'Something went wrong. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setResult(null);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4" data-testid="contact-form-modal">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={handleClose} />
      <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
        <div className="px-6 py-5 flex items-center justify-between" style={{ background: 'linear-gradient(135deg, #E84993 0%, #B565CC 100%)' }}>
          <div>
            <h2 className="text-lg font-medium text-white" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{config.title}</h2>
            <p className="text-xs text-white/70 mt-0.5">{config.subtitle}</p>
          </div>
          <button onClick={handleClose} className="text-white/70 hover:text-white transition-colors" data-testid="close-contact-form">
            <X className="w-5 h-5" />
          </button>
        </div>

        {result?.success ? (
          <div className="p-8 text-center" data-testid="contact-form-success">
            <div className="w-14 h-14 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <Send className="w-6 h-6 text-green-600" />
            </div>
            <h3 className="text-lg font-medium mb-2">Message Sent!</h3>
            <p className="text-sm text-gray-500 mb-6">{result.message}</p>
            <button onClick={handleClose} className="px-6 py-2.5 text-white text-sm rounded-lg transition-colors" style={{ background: 'linear-gradient(135deg, #E84993 0%, #B565CC 100%)' }}>
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 space-y-4" data-testid="contact-form-fields">
            {result && !result.success && (
              <div className="bg-red-50 text-red-600 text-sm px-4 py-3 rounded-lg">{result.message}</div>
            )}
            <div>
              <label className="block text-xs uppercase tracking-wider text-gray-500 mb-1.5">Full Name *</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                required
                className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#C4A484] transition-colors"
                data-testid="contact-name-input"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-gray-500 mb-1.5">Email *</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                required
                className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#C4A484] transition-colors"
                data-testid="contact-email-input"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-gray-500 mb-1.5">Phone</label>
              <input
                type="tel"
                value={form.phone}
                onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#C4A484] transition-colors"
                data-testid="contact-phone-input"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-gray-500 mb-1.5">Message</label>
              <textarea
                value={form.message}
                onChange={(e) => setForm((p) => ({ ...p, message: e.target.value }))}
                rows={4}
                placeholder={PLACEHOLDERS[inquiryType]}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#C4A484] transition-colors resize-none"
                data-testid="contact-message-input"
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="w-full py-3.5 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              style={{ background: 'linear-gradient(135deg, #E84993 0%, #B565CC 100%)' }}
              data-testid="contact-submit-btn"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {submitting ? 'Sending...' : 'Send Message'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};
